import { AGE_BANDS, NOTE_TOPICS, USER_REPORT_REASONS } from '@platform/core/moderation';
import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ApiError,
  type RiskLevel,
  reportUser,
  type SessionDebrief,
  saveDebrief,
  sessionDebrief,
} from '../api.ts';
import { formatDuration, formatWhen } from '../format.ts';
import { useAnnounce } from '../hooks/useAnnounce.tsx';
import { apiMessage } from '../messages.ts';

/**
 * Debriefing post-sessione (sezione 5.8, mock 2 `v-debrief`).
 *
 * "Facoltativo, tutto a scelte rapide: fascia d'età percepita o dichiarata,
 * tema prevalente, livello di rischio. Un campo obbligatorio dopo ogni chiamata
 * viene riempito di «ok» entro due settimane e smette di servire a qualcosa."
 *
 * Da qui si esce **sempre** con un tocco, anche senza compilare niente, e "si
 * può chiudere il turno subito dopo una sessione pesante, senza spiegazioni".
 * Le due uscite stanno una accanto all'altra e nessuna delle due è nascosta
 * dietro una conferma.
 *
 * Il campo per scrivere compare solo sopra "da seguire": è la regola della
 * sezione 5.8, e la applica anche il server — un limite che vive solo qui si
 * aggira con una richiesta scritta a mano (R12).
 */

/**
 * "Non indicata" è una scelta, non l'assenza di una scelta.
 *
 * Senza, un tocco sbagliato su "critica" non si annulla — e il livello di
 * rischio **decide** se il campo di testo libero compare (AC13). Il valore
 * vuoto viaggia come `null`, che è ciò che la rotta accetta.
 */
const RISKS: { value: RiskLevel | ''; labelKey: string }[] = [
  { value: '', labelKey: 'debrief.risk_none' },
  { value: 'low', labelKey: 'debrief.risk_low' },
  { value: 'follow_up', labelKey: 'debrief.risk_follow_up' },
  { value: 'critical', labelKey: 'debrief.risk_critical' },
];

type Props = {
  sessionId: string;
  /**
   * Questa conversazione era rimasta indietro.
   *
   * Succede quando una chiamata nuova arriva prima che il debriefing della
   * precedente sia compilato: la coda ne tiene più di uno e si comincia dal
   * più vecchio (`ConsolePage`). Senza dirlo, la schermata comparirebbe una
   * seconda volta parlando di una conversazione che non è quella appena
   * chiusa, e chi la compila attribuirebbe le risposte a quella sbagliata.
   */
  earlier?: boolean;
  /** Il titolo riceve il fuoco quando questa schermata subentra alla chiamata. */
  headingRef?: React.RefObject<HTMLElement | null>;
  /**
   * Il fuso dell'ente, per scrivere quando è cominciata la conversazione.
   *
   * Un debriefing recuperato (`pendingNotes`) può arrivare giorni dopo: senza
   * l'orario di inizio, "audio · 04:32" non basta a ricordare *quale*
   * conversazione fosse, specialmente con più di un debriefing rimasto
   * indietro dallo stesso turno.
   */
  timezone?: string;
  /** Torna in ascolto senza chiudere il turno. */
  onDone: () => void;
  /** Salva e chiude il turno, senza spiegazioni (sezione 5.8). */
  onEndShift: () => void;
};

export function Debrief({ sessionId, earlier, headingRef, timezone, onDone, onEndShift }: Props) {
  const { t, i18n } = useTranslation();

  const [loaded, setLoaded] = useState<SessionDebrief>();
  const [risk, setRisk] = useState<RiskLevel | ''>('');
  const [ageBand, setAgeBand] = useState('');
  const [topic, setTopic] = useState('');
  const [text, setText] = useState('');
  const [referred, setReferred] = useState(false);
  const [busy, setBusy] = useState(false);
  const [reporting, setReporting] = useState(false);
  const [reportReason, setReportReason] = useState('');
  const [reportText, setReportText] = useState('');
  /**
   * La regione persistente e il banner, come un nodo solo.
   *
   * Qui erano due, e portavano **lo stesso testo**: l'`Announcer` stabile più
   * un `<p role="status">` che nasceva insieme al messaggio. Il primo giro era
   * muto — una regione che compare già piena non viene annunciata — e dal
   * secondo in poi lo stesso esito veniva letto **due volte**. AK1 lasciava
   * questo componente fuori dall'astrazione dicendo che il suo `say` porta un
   * secondo argomento: non è vero, ne porta uno solo, e ci entrava già allora
   * (`docs/DECISIONI.md`, AO12).
   */
  const { say, announcer } = useAnnounce();
  const reportHeadingRef = useRef<HTMLHeadingElement>(null);
  /**
   * La prima delle scelte del motivo: è lì che il fuoco torna quando manca.
   *
   * Dire "scegli un motivo" e lasciare il fuoco sul pulsante di invio obbliga
   * chi naviga da tastiera a risalire il modulo a ritroso per cercare il campo
   * di cui si parla — e chi usa uno screen reader a indovinare dov'è.
   */
  const firstReasonRef = useRef<HTMLInputElement>(null);
  /** Il pulsante che apre la segnalazione: è lì che il fuoco torna. */
  const openReportRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    let cancelled = false;
    void sessionDebrief(sessionId)
      .then((result) => {
        if (!cancelled) setLoaded(result);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  /**
   * Entrando nella segnalazione il fuoco la segue.
   *
   * È una schermata che sostituisce il modulo sotto le dita: senza, chi naviga
   * da tastiera resterebbe su un pulsante appena smontato e ricomincerebbe a
   * tabulare dall'inizio (U2).
   */
  useEffect(() => {
    if (reporting) reportHeadingRef.current?.focus();
  }, [reporting]);

  const allowsText = risk === 'follow_up' || risk === 'critical';

  const save = async (then: 'listen' | 'end_shift'): Promise<void> => {
    if (busy) return;
    setBusy(true);
    try {
      await saveDebrief(sessionId, {
        riskLevel: risk === '' ? null : risk,
        ageBand: ageBand === '' ? null : ageBand,
        topic: topic === '' ? null : topic,
        text: allowsText && text.trim() ? text.trim() : null,
        referredExternally: referred,
      });
      if (then === 'end_shift') onEndShift();
      else onDone();
    } catch (error) {
      const key = error instanceof ApiError ? error.messageKey : 'errors.unexpected';
      say(`${t('debrief.save_failed')} ${apiMessage(t, key)}`, 'danger');
    } finally {
      setBusy(false);
    }
  };

  const sendReport = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    if (busy) return;
    if (!reportReason) {
      /*
        Qui veniva annunciata `report_user.reason_label`, cioè l'**etichetta**
        del gruppo — "Cosa è successo" — che non è un errore: chi non vede
        sentiva ripetere il nome del campo e nessuno gli diceva che l'invio non
        era partito. Adesso c'è una frase che dice cosa fare, e il fuoco va
        dove si fa.
      */
      say(t('report_user.reason_required'), 'danger');
      firstReasonRef.current?.focus();
      return;
    }
    setBusy(true);
    try {
      const result = await reportUser(sessionId, {
        reason: reportReason,
        ...(reportText.trim() ? { text: reportText.trim() } : {}),
      });
      setReporting(false);
      say(result.status === 'already_reported' ? t('report_user.already') : t('report_user.sent'));
      setLoaded((previous) => (previous ? { ...previous, reported: true } : previous));
    } catch (error) {
      const key = error instanceof ApiError ? error.messageKey : 'errors.unexpected';
      say(`${t('report_user.failed')} ${apiMessage(t, key)}`, 'danger');
    } finally {
      setBusy(false);
    }
  };

  if (reporting) {
    return (
      <section className="card" aria-labelledby="report-heading">
        {/* `h3`: il titolo di pagina è quello della console, e questa è una scheda. */}
        <h3 id="report-heading" ref={reportHeadingRef} tabIndex={-1}>
          {t('report_user.title')}
        </h3>
        {/*
          `{announcer}` **subito dopo il titolo**, esattamente come nell'altro
          ramo — la stessa disciplina di `NewTenantSection` in `PlatformPage`.

          Qui non c'era affatto: la regione live stava solo nel ramo
          principale, e tutti gli esiti della segnalazione passavano da `say`
          per finire in uno stato che nessuno disegnava. Se l'invio falliva, il
          pulsante tornava da "Invio…" a "Invia la segnalazione" e non
          succedeva altro: nessun banner, nessun annuncio, nessun modo di
          sapere che la segnalazione non era partita.

          E montarlo qui in una posizione **diversa** non basterebbe: i due
          rami hanno la stessa radice, quindi React confronta i figli per
          posizione, e tornando indietro troverebbe un `p` dove c'era un
          frammento — distruggerebbe la regione live e ne monterebbe una già
          piena, che è precisamente ciò che `useAnnounce` esiste per rendere
          impossibile (AM4). Primo figlio dopo il titolo di qua, primo figlio
          dopo il titolo di là.
        */}
        {announcer}
        {/*
          Su una sessione anonima la segnalazione resta documentazione, ma non
          porta a un blocco: non c'è un account, non c'è un'impronta (AC16). Si
          dice prima, non dopo aver premuto il pulsante.
        */}
        {loaded?.anonymous ? (
          <p className="banner banner--warn">{t('report_user.anonymous_note')}</p>
        ) : null}

        <form onSubmit={(event) => void sendReport(event)}>
          <fieldset className="presence">
            <legend>{t('report_user.reason_label')}</legend>
            <div className="presence__options">
              {USER_REPORT_REASONS.map((reason, index) => (
                <label
                  key={reason}
                  className={
                    reportReason === reason ? 'presence__option is-on' : 'presence__option'
                  }
                >
                  <input
                    type="radio"
                    name="user-report-reason"
                    ref={index === 0 ? firstReasonRef : undefined}
                    value={reason}
                    checked={reportReason === reason}
                    onChange={() => setReportReason(reason)}
                  />
                  <span>{t(`report_user.reason_${reason}`)}</span>
                </label>
              ))}
            </div>
          </fieldset>

          <div className="field">
            <label htmlFor="user-report-text">{t('report_user.text_label')}</label>
            <textarea
              id="user-report-text"
              rows={3}
              maxLength={2000}
              value={reportText}
              onChange={(event) => setReportText(event.target.value)}
            />
          </div>

          {/*
            In fila e non impilati a tutta larghezza: uno invia, l'altro torna
            indietro, e due pulsanti larghi quanto lo schermo li fanno sembrare
            la stessa cosa. "Annulla" è servizio, quindi `--quiet`.
          */}
          <div className="actions">
            {/* Cambia etichetta, non si disabilita: un pulsante disabilitato esce
                dal percorso di tabulazione e scarica il fuoco su `body` (X3). */}
            <button type="submit" className="button button--primary">
              {t(busy ? 'report_user.sending' : 'report_user.send')}
            </button>
            <button
              type="button"
              className="button button--quiet"
              onClick={() => {
                setReporting(false);
                openReportRef.current?.focus();
              }}
            >
              {t('report_user.cancel')}
            </button>
          </div>
        </form>
      </section>
    );
  }

  return (
    <section className="card" aria-labelledby="debrief-heading">
      {/* `h3`: il titolo di pagina è quello della console, e questa è una scheda. */}
      <h3
        id="debrief-heading"
        ref={headingRef as React.RefObject<HTMLHeadingElement> | undefined}
        tabIndex={-1}
      >
        {t('debrief.title')}
      </h3>

      {/* Regione montata prima e vuota: una che nasce piena non parla (R10). */}
      {announcer}
      {/*
        Di quale conversazione si parla, e che compilare non è obbligatorio:
        una riga sola sotto il titolo, che è il posto della riga che spiega la
        scheda. Erano due paragrafi consecutivi con lo stesso peso del resto.
      */}
      <p className="card__sub">
        {t('debrief.summary', {
          // L'orario guida la frase: è quello che permette di riconoscere la
          // conversazione quando il debriefing si scrive giorni dopo (coda
          // `pendingNotes`, BI4), specie con più di un debriefing nello stesso
          // turno. Stesso ordine della tabella "Le mie sessioni" (mock
          // `v-history`): data, canale, durata.
          startedAt: formatWhen(loaded?.startedAt ?? null, i18n.resolvedLanguage, timezone) || '—',
          // Nessun canale di riserva finché non si sa quale sia davvero: un
          // video o una chat appena chiusi non devono comparire per un
          // istante come "audio" (stesso principio di `formatDuration(null)`
          // qui sotto, che non inventa mai un tempo).
          channel: loaded ? t(`debrief.channel_${loaded.channel}`) : '—',
          duration: formatDuration(loaded?.durationSeconds ?? null),
        })}{' '}
        {t('debrief.optional')}
      </p>
      {earlier ? <p className="banner banner--warn">{t('debrief.earlier')}</p> : null}

      {/*
        Il debriefing già scritto non si riscrive: la nota è la sola traccia che
        una sessione lascia, e una traccia riscrivibile documenta la seconda
        versione (AC3). Qui si dice, invece di far scoprire un errore al
        salvataggio.
      */}
      {loaded?.note ? <p className="banner">{t('debrief.already')}</p> : null}

      {loaded?.note ? null : (
        <>
          <fieldset className="presence">
            <legend>{t('debrief.risk_label')}</legend>
            <div className="presence__options">
              {RISKS.map((choice) => (
                <label
                  key={choice.value}
                  className={risk === choice.value ? 'presence__option is-on' : 'presence__option'}
                >
                  <input
                    type="radio"
                    name="debrief-risk"
                    value={choice.value}
                    checked={risk === choice.value}
                    onChange={() => setRisk(choice.value)}
                  />
                  <span>{t(choice.labelKey)}</span>
                </label>
              ))}
            </div>
          </fieldset>
          <p className="muted">{t('debrief.risk_hint')}</p>

          <div className="field">
            <label htmlFor="debrief-age">{t('debrief.age_label')}</label>
            <select
              id="debrief-age"
              value={ageBand}
              onChange={(event) => setAgeBand(event.target.value)}
            >
              <option value="">{t('debrief.age_none')}</option>
              {AGE_BANDS.map((band) => (
                <option key={band} value={band}>
                  {t(`debrief.age_${band}`)}
                </option>
              ))}
            </select>
          </div>

          <div className="field">
            <label htmlFor="debrief-topic">{t('debrief.topic_label')}</label>
            <select
              id="debrief-topic"
              value={topic}
              onChange={(event) => setTopic(event.target.value)}
            >
              <option value="">{t('debrief.topic_none')}</option>
              {NOTE_TOPICS.map((entry) => (
                <option key={entry} value={entry}>
                  {t(`debrief.topic_${entry}`)}
                </option>
              ))}
            </select>
          </div>

          {/*
            "Il testo libero compare solo se il volontario segna «da seguire» o
            «critica»" (sezione 5.8). Compare, non si abilita: un campo grigio
            che non si può scrivere è una domanda a cui non si capisce come si
            risponde.
          */}
          {allowsText ? (
            <div className="field">
              <label htmlFor="debrief-text">{t('debrief.text_label')}</label>
              <textarea
                id="debrief-text"
                rows={4}
                maxLength={2000}
                value={text}
                onChange={(event) => setText(event.target.value)}
                aria-describedby="debrief-text-hint"
              />
              <p className="muted" id="debrief-text-hint">
                {t('debrief.text_hint')}
              </p>
            </div>
          ) : null}

          {/*
            Casella prima, etichetta accanto — non sopra.

            `.field` impila etichetta e controllo, che per una tendina è giusto
            e per una spunta no: qui l'ordine nel documento è casella-poi-
            etichetta, quindi la colonna metteva il quadratino **sopra** la sua
            frase, al contrario di ogni altro campo del prodotto. Nessuno se ne
            era accorto perché questa schermata compare solo dopo una
            conversazione, e nella galleria non si disegnava affatto.

            `field--check` è la stessa classe della spunta nella barra dei
            filtri del registro accessi: una spunta si comporta allo stesso modo
            ovunque.
          */}
          <div className="field field--inline field--check">
            <input
              type="checkbox"
              id="debrief-referred"
              checked={referred}
              onChange={(event) => setReferred(event.target.checked)}
            />
            <label htmlFor="debrief-referred">{t('debrief.referred_label')}</label>
          </div>
        </>
      )}

      {/*
        Cinque pulsanti larghi quanto lo schermo, impilati, sono cinque uscite
        che sembrano la stessa cosa — e questo è il momento in cui si esce da
        una conversazione difficile. Nessuna delle uscite è stata tolta: il
        debriefing è facoltativo e da qui si deve poter uscire senza compilare
        niente (sezione 5.8). Sono state **ordinate**.

        Due gesti, e la riga dice quale: la prima riga torna in ascolto — con
        o senza compilare — la seconda chiude il turno, che è la cosa che si fa
        una volta a sera. Su schermo stretto vanno a capo, non diventano una
        colonna di pulsanti tutti uguali (`.actions`).
      */}
      <div className="actions">
        {loaded?.note ? (
          <button type="button" className="button button--primary" onClick={onDone}>
            {t('debrief.skip')}
          </button>
        ) : (
          <>
            <button
              type="button"
              className="button button--primary"
              onClick={() => void save('listen')}
            >
              {t(busy ? 'debrief.saving' : 'debrief.save')}
            </button>
            <button type="button" className="button" onClick={onDone}>
              {t('debrief.skip')}
            </button>
          </>
        )}
      </div>

      {/*
        "Possibilità di chiudere il turno subito dopo una sessione pesante,
        senza spiegazioni" (sezione 5.8). Senza queste due uscite, chi voleva
        solo smettere doveva salvare una nota vuota — e la nota si scrive una
        volta sola (AC3), quindi bruciava la possibilità di scriverla domani.
        Restano a vista e a un tocco: più leggere, perché chiudere il turno non
        è ciò che si fa alla fine di ogni conversazione.
      */}
      <div className="actions">
        {loaded?.note ? null : (
          <button
            type="button"
            className="button button--quiet"
            onClick={() => void save('end_shift')}
          >
            {t('debrief.save_and_end')}
          </button>
        )}
        <button type="button" className="button button--quiet" onClick={onEndShift}>
          {t('debrief.skip_and_end')}
        </button>
      </div>

      {/*
        Segnalare chi ha chiamato si fa da qui: è il momento in cui si ha in
        mente cosa è successo (AC16). Sotto le uscite, perché la maggior parte
        delle conversazioni non ha niente da segnalare.
      */}
      {loaded?.reported ? (
        <p className="muted">{t('report_user.already')}</p>
      ) : (
        <div className="actions">
          <button
            type="button"
            className="button"
            ref={openReportRef}
            onClick={() => setReporting(true)}
          >
            {t('report_user.open')}
          </button>
        </div>
      )}

      {/*
        Le tre note che spiegano invece di chiedere qualcosa: come si legge
        questa nota, che le risposte sono stimate, e che il turno si chiude
        quando si vuole. Restano nel documento — `details` le toglie alla vista,
        non a chi cerca nel testo — ma non stanno più fra il modulo e le uscite.
      */}
      <details className="more">
        <summary>{t('debrief.more')}</summary>
        {loaded?.anonymous ? <p className="muted">{t('debrief.anonymous_note')}</p> : null}
        {loaded?.note ? null : <p className="muted">{t('debrief.declared_note')}</p>}
        <p className="muted">{t('debrief.close_hint')}</p>
      </details>
    </section>
  );
}
